/**
 * Enhanced API Service
 *
 * This service provides a standardized way to make API calls with proper
 * response handling based on the backend API documentation.
 */

import axios from 'axios';
import {API_CONFIG} from '../../../config/api.config';
import {
  handleApiResponse,
  handleApiError,
  handleWhatsAppResponse,
  handleMessageResponse,
  handleCustomerResponse,
  handleHistoryResponse,
} from './responseHandler';

/**
 * Create axios instance with default configuration
 */
const createApiInstance = (
  baseURL = `${API_CONFIG.BASE_URL}/api`,
  timeout = 30000,
) => {
  const instance = axios.create({
    baseURL,
    timeout,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor to add auth token and language
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add Accept-Language header based on current locale
      let locale = 'en';
      try {
        // First try to get from simple locale storage
        const simpleLocale = localStorage.getItem('locale');
        if (simpleLocale) {
          locale = simpleLocale;
        } else {
          // Fallback to complex locale object
          const localeObj = localStorage.getItem('app_locale');
          if (localeObj) {
            const parsed = JSON.parse(localeObj);
            locale = parsed.locale || 'en';
          }
        }
      } catch (e) {
        locale = 'en';
      }
      config.headers['Accept-Language'] = locale === 'ar' ? 'ar' : 'en';

      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  // Response interceptor to handle global errors like 401
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      // Handle unauthorized errors globally
      if (error.response?.status === 401) {
        // Clear local storage tokens but don't force a page reload
        // as that can cause infinite loops
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('authPayload');

        // Let React Router handle the redirect securely
        window.dispatchEvent(new CustomEvent('unauthorized'));
      }
      return Promise.reject(error);
    },
  );

  return instance;
};

/**
 * Base API service class
 */
class ApiService {
  constructor(baseURL, timeout = 30000) {
    this.api = createApiInstance(baseURL, timeout);
  }

  /**
   * Make GET request with response handling
   * @param {string} url - API endpoint
   * @param {Object} config - Axios config
   * @param {Function} responseHandler - Custom response handler
   * @returns {Promise<Object>} - Standardized response
   */
  async get(url, config = {}, responseHandler = handleApiResponse) {
    try {
      const response = await this.api.get(url, config);
      return responseHandler(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * Make POST request with response handling
   * @param {string} url - API endpoint
   * @param {Object} data - Request data
   * @param {Object} config - Axios config
   * @param {Function} responseHandler - Custom response handler
   * @returns {Promise<Object>} - Standardized response
   */
  async post(url, data = {}, config = {}, responseHandler = handleApiResponse) {
    try {
      const response = await this.api.post(url, data, config);
      return responseHandler(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * Make PUT request with response handling
   * @param {string} url - API endpoint
   * @param {Object} data - Request data
   * @param {Object} config - Axios config
   * @param {Function} responseHandler - Custom response handler
   * @returns {Promise<Object>} - Standardized response
   */
  async put(url, data = {}, config = {}, responseHandler = handleApiResponse) {
    try {
      const response = await this.api.put(url, data, config);
      return responseHandler(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * Make DELETE request with response handling
   * @param {string} url - API endpoint
   * @param {Object} config - Axios config
   * @param {Function} responseHandler - Custom response handler
   * @returns {Promise<Object>} - Standardized response
   */
  async delete(url, config = {}, responseHandler = handleApiResponse) {
    try {
      const response = await this.api.delete(url, config);
      return responseHandler(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}

/**
 * WhatsApp API Service
 */
class WhatsAppApiService extends ApiService {
  constructor(baseURL = `${API_CONFIG.BASE_URL}/api/whatsapp`) {
    super(baseURL);
  }

  /**
   * Get WhatsApp connection status
   * @returns {Promise<Object>} - WhatsApp status response
   */
  async getStatus() {
    return this.get('/status', {}, handleWhatsAppResponse);
  }

  /**
   * Get WhatsApp QR code for connection
   * @returns {Promise<Object>} - QR code response
   */
  async connect() {
    return this.get('/connect', {}, handleWhatsAppResponse);
  }

  /**
   * Check if phone number is registered on WhatsApp
   * @param {string} phoneNumber - Phone number to check
   * @returns {Promise<Object>} - Phone number check response
   */
  async checkWhatsAppNumber(phoneNumber) {
    return this.post(
      '/check-whatsapp-number',
      {phoneNumber},
      {},
      handleWhatsAppResponse,
    );
  }
}

/**
 * Message API Service
 */
class MessageApiService extends ApiService {
  constructor(baseURL = `${API_CONFIG.BASE_URL}/api/messages`) {
    super(baseURL);
  }

  /**
   * Send single message
   * @param {Object} messageData - Message data
   * @returns {Promise<Object>} - Message send response
   */
  async sendSingle(messageData) {
    return this.post('/send-single', messageData, {}, handleMessageResponse);
  }

  /**
   * Send bulk messages
   * @param {Object} bulkData - Bulk message data
   * @returns {Promise<Object>} - Bulk message response
   */
  async sendBulk(bulkData) {
    return this.post('/sendBulk', bulkData, {}, handleMessageResponse);
  }
}

/**
 * Customer API Service
 */
class CustomerApiService extends ApiService {
  constructor(baseURL = `${API_CONFIG.BASE_URL}/api/customers`) {
    super(baseURL);
  }

  /**
   * Get customers list
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} - Customers list response
   */
  async getCustomers(params = {}) {
    return this.get('', {params}, handleCustomerResponse);
  }

  /**
   * Get customer by ID
   * @param {string|number} id - Customer ID
   * @returns {Promise<Object>} - Customer response
   */
  async getCustomer(id) {
    return this.get(`/${id}`, {}, handleCustomerResponse);
  }

  /**
   * Create new customer
   * @param {Object} customerData - Customer data
   * @returns {Promise<Object>} - Customer creation response
   */
  async createCustomer(customerData) {
    return this.post('', customerData, {}, handleCustomerResponse);
  }

  /**
   * Update customer
   * @param {string|number} id - Customer ID
   * @param {Object} customerData - Updated customer data
   * @returns {Promise<Object>} - Customer update response
   */
  async updateCustomer(id, customerData) {
    return this.put(`/${id}`, customerData, {}, handleCustomerResponse);
  }

  /**
   * Delete customer
   * @param {string|number} id - Customer ID
   * @returns {Promise<Object>} - Customer deletion response
   */
  async deleteCustomer(id) {
    return this.delete(`/${id}`, {}, handleCustomerResponse);
  }

  /**
   * Sync WhatsApp contacts
   * @returns {Promise<Object>} - Sync response
   */
  async syncWhatsApp() {
    return this.get('/sync-whatsapp', {}, handleCustomerResponse);
  }

  /**
   * Get customer categories
   * @returns {Promise<Object>} - Categories response
   */
  async getCategories() {
    return this.get('/categories', {}, handleCustomerResponse);
  }
}

/**
 * History API Service
 */
class HistoryApiService extends ApiService {
  constructor(baseURL = `${API_CONFIG.BASE_URL}/api/history`) {
    super(baseURL);
  }

  /**
   * Get user message history
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} - History response
   */
  async getUserHistory(params = {}) {
    return this.get('/user-history', {params}, handleHistoryResponse);
  }
}

/**
 * User API Service
 */
class UserApiService extends ApiService {
  constructor(baseURL = `${API_CONFIG.BASE_URL}/api/users`) {
    super(baseURL);
  }

  /**
   * Get users list
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} - Users list response
   */
  async getUsers(params = {}) {
    return this.get('', {params}, handleApiResponse);
  }

  /**
   * Get user by ID
   * @param {string|number} id - User ID
   * @returns {Promise<Object>} - User response
   */
  async getUser(id) {
    // Validate ID before making API call
    if (!id || id === 'undefined' || id === 'null' || id === '') {
      throw new Error('Invalid user ID provided');
    }
    return this.get(`/${id}`, {}, handleApiResponse);
  }

  /**
   * Verify user password
   * @param {string} password - Password to verify
   * @returns {Promise<Object>} - Password verification response
   */
  async verifyPassword(password) {
    return this.post('/verify-password', {password}, {}, handleApiResponse);
  }
}

/**
 * Auth API Service
 */
class AuthApiService extends ApiService {
  constructor(baseURL = `${API_CONFIG.BASE_URL}/api/auth`) {
    super(baseURL);
  }

  /**
   * Login user
   * @param {Object} credentials - Login credentials
   * @returns {Promise<Object>} - Login response
   */
  async login(credentials) {
    return this.post('/login', credentials, {}, handleApiResponse);
  }

  /**
   * Register user
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} - Registration response
   */
  async register(userData) {
    return this.post('/register', userData, {}, handleApiResponse);
  }
}

/**
 * Settings API Service
 */
class SettingsApiService extends ApiService {
  constructor(baseURL = `${API_CONFIG.BASE_URL}/api`) {
    super(baseURL);
  }

  async getAll() {
    return this.get('/admin/settings', {}, handleApiResponse);
  }

  async getByGroup(group) {
    return this.get(`/admin/settings/group/${group}`, {}, handleApiResponse);
  }

  async getSetting(key) {
    return this.get(`/admin/settings/${key}`, {}, handleApiResponse);
  }

  async updateBulk(settings) {
    return this.put('/admin/settings', settings, {}, handleApiResponse);
  }

  async updateSetting(key, data) {
    return this.put(`/admin/settings/${key}`, data, {}, handleApiResponse);
  }

  async uploadImage(key, formData) {
    return this.post(
      `/admin/settings/${key}/image`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
      handleApiResponse,
    );
  }

  async deleteImage(key) {
    return this.delete(`/admin/settings/${key}/image`, {}, handleApiResponse);
  }
}

// Create service instances
export const whatsappApi = new WhatsAppApiService();
export const messageApi = new MessageApiService();
export const customerApi = new CustomerApiService();
export const historyApi = new HistoryApiService();
export const userApi = new UserApiService();
export const authApi = new AuthApiService();
export const settingsService = new SettingsApiService();

// Export service classes for custom instances
export {
  ApiService,
  WhatsAppApiService,
  MessageApiService,
  CustomerApiService,
  HistoryApiService,
  UserApiService,
  AuthApiService,
  SettingsApiService,
};

export default {
  whatsappApi,
  messageApi,
  customerApi,
  historyApi,
  userApi,
  authApi,
  settingsService,
};
